import { Component, ElementRef, HostListener, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { AlertService, AuthService, HttpService, MenuService, TokenService } from 'src/app/shared/services';
import { CurrencyService } from 'src/app/shared/services/currency/currency.service';
import { TranslateService } from '@ngx-translate/core';
import { environment as env, environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { SupportedLanguageConstant, WebTypeConstant } from 'src/app/shared/constants/common-constant';
import { BaseResponse } from 'src/app/models/base.interface';
import { ContentMerchantMarquee, ContentMerchantMarqueeShowList, UnAgentMarqueeList, ContentUnAgentMarquee } from 'src/app/models/shared/layout/header.interface';
import { LanguageService } from 'src/app/shared/services/language/language.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent {

    languageImage: string = '';
    currencyList: any[] = [];
    selectedCurrency: any = null;
    showCurrencyDropdown: boolean = false;
    webType: any = '';
    webTypeConstant = WebTypeConstant;
    userInfo: any;
    currentLanguage = this.token.getLocalToken(env.TokenKeys.Language) || env.DefaultLanguageCode;
    marqueeMessage: any = "";
    merchantMarqueeList: ContentMerchantMarquee[] = [];
    unAgentMarqueeList: ContentUnAgentMarquee[] = [];
    unAgentMarqueeMultipleList: ContentUnAgentMarquee[] = [];
    unAgentMarqueeSingleList: ContentUnAgentMarquee[] = [];
    accessList: any[] = [];
    languageConstant = SupportedLanguageConstant;

    // marquee animation
    startPos = '0px';
    endPos = '0px';
    duration = 0;

    private destroy$ = new Subject<void>();

    @ViewChild('messageWrapper') containerRef!: ElementRef;
    @ViewChild('scrollingText') contentRef!: ElementRef;

    constructor(
        private token: TokenService,
        protected translate: TranslateService,
        private router: Router,
        private currencyService: CurrencyService,
        private httpService: HttpService,
        private alertService: AlertService,
        private authService: AuthService,
        private tokenService: TokenService,
        private languageService: LanguageService,
        private spinner: NgxSpinnerService,
        private menuService: MenuService
    ) { }

    async ngOnInit() {
        this.accessList = this.menuService.getAccessListByName('Others');
        this.getLanguageIcon();
        this.webType = this.token.getLocalToken(env.TokenKeys.WebType);

        const userInfoString = this.token.getLocalToken(env.TokenKeys.UserInfo);
        this.userInfo = userInfoString ? JSON.parse(userInfoString) : null;

        if ([
            WebTypeConstant.MERCHANT,
            WebTypeConstant.MERCHANTSTAFF,
            WebTypeConstant.AGENTUNLIMITED,
            WebTypeConstant.AGENTSTAFFUNLIMITED
        ].includes(this.webType)
        ) {
            this.showCurrencyDropdown = true;
            this.currencyList = this.currencyService.getCurrencyList();

            this.currencyService.selectedCurrency$.subscribe(currency => {
                this.selectedCurrency = currency;
            });

            this.selectedCurrency = this.currencyService.getCurrency();
        }

        this.currentLanguage = this.languageService.getLanguage();

        this.languageService.selectedLanguage$
            .pipe(takeUntil(this.destroy$))
            .subscribe(lang => {
                this.currentLanguage = lang || environment.DefaultLanguageCode;
                this.getLanguageIcon();
                this.getMarqueeList();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }


    toggleSidebar() {
        if (window.innerWidth <= 768) {
            document.body.classList.toggle('sidebar-open');
        } else {
            document.body.classList.toggle('sidebar-hidden');
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        // Only close sidebar if it's open and screen size is small
        if (window.innerWidth <= 768) {
            // document.body.classList.remove('sidebar-open');
            document.body.classList.remove('sidebar-hidden'); // Ensure it remains openable on larger screens
            document.body.classList.remove('sidebar-open');
        }
    }

    getLanguageIcon() {
        const langCode = this.currentLanguage;
        this.languageImage = '/assets/images/lang/' + langCode.toLowerCase() + '.png';
    }

    changeLanguage(lang: string) {
        this.languageService.setLanguage(lang);
    }

    changeCurrency(currency: any) {
        this.currencyService.setCurrency(currency);
    }

    logout() {
        this.authService.logout().subscribe(
            (success) => {
                if (success) {
                    this.router.navigate(['/auth/login']);
                } else {
                    console.error('Logout failed');
                }
            },
            (error) => {
                console.error('Logout error', error);
            }
        );
    }

    syncAdminMenu() {
        this.httpService.post('admin', 'Admin', 'SyncAdminMenu', {})
            .subscribe((resp: any) => {
                if (resp && resp.code == 1) {
                    const menuList = this.authService.getRestructuredMenuList(resp.data.adminMenuList);
                    this.tokenService.setLocalToken(environment.TokenKeys.MenuList, JSON.stringify(menuList));
                    this.alertService.success('sync');
                    window.location.reload();
                } else {
                    this.alertService.error('sync');
                }
            });
    }

    getMerchantMarqueeList(): Promise<boolean> {
        return new Promise((resolve) => {
            const payload = {
                languageType: this.currentLanguage
            };

            this.httpService.post('content', 'ContentMerchantMarquee', 'GetContentMerchantMarqueeShowList', payload, false)
                .subscribe((resp: BaseResponse<ContentMerchantMarqueeShowList>) => {
                    if (resp && resp.code == 1) {
                        this.merchantMarqueeList = (resp.data.contentMerchantMarqueeShowList || [])
                            .sort((a, b) => a.sequence - b.sequence);
                    }
                    resolve(resp?.code === 1);
                }, () => resolve(false));
        });
    }

    getUnAgentMarqueeSingleList(): Promise<boolean> {
        return new Promise((resolve) => {
            const payload = {
                languageType: this.currentLanguage
            };

            this.httpService.post('content', 'ContentUnAgentMarqueeSingle', 'GetContentUnAgentMarqueeSingleShowList', payload, false)
                .subscribe((resp: BaseResponse<UnAgentMarqueeList>) => {
                    if (resp && resp.code == 1) {
                        this.unAgentMarqueeSingleList = resp.data.contentUnAgentMarqueeSingleShowList ?? [];
                    }
                    resolve(resp?.code === 1);
                }, () => resolve(false));
        });
    }

    getUnAgentMarqueeMultipleList(): Promise<boolean> {
        return new Promise((resolve) => {
            const payload = {
                languageType: this.currentLanguage
            };

            this.httpService.post('content', 'ContentUnAgentMarqueeMultiple', 'GetContentUnAgentMarqueeMultipleShowList', payload, false)
                .subscribe((resp: BaseResponse<UnAgentMarqueeList>) => {
                    if (resp && resp.code == 1) {
                        this.unAgentMarqueeMultipleList = resp.data.contentUnAgentMarqueeMultipleShowList ?? [];
                    }
                    resolve(resp?.code === 1);
                }, () => resolve(false));
        });
    }


    setScrollingDuration(): void {
        const speed = 80; // pixels per second

        const containerWidth = this.containerRef.nativeElement.offsetWidth;
        const contentWidth = this.contentRef.nativeElement.offsetWidth;
        const distance = containerWidth + Math.max(containerWidth, contentWidth);

        this.duration = distance / speed;

        // set CSS vars for animation distances, including units 'px'
        this.startPos = `${containerWidth}px`;
        this.endPos = `-${contentWidth}px`;
    }

    hasAccess(actionName: string): boolean {
        return this.accessList.some(access => access.name === actionName);
    }

    async getMarqueeList() {
        this.marqueeMessage = "";

        if (this.webType === WebTypeConstant.MERCHANT || this.webType === WebTypeConstant.MERCHANTSTAFF) {
            if (this.hasAccess("GetContentMerchantMarqueeShowList")) {
                await this.getMerchantMarqueeList();

                this.marqueeMessage = this.merchantMarqueeList
                    .map((m, i) => `${i + 1}. ${m.message}`)
                    .join(' ｜ ');

                if (this.marqueeMessage.trim() != '') {
                    setTimeout(() => this.setScrollingDuration(), 0);
                }
            }

        } else if (this.webType === WebTypeConstant.AGENTUNLIMITED || this.webType === WebTypeConstant.AGENTSTAFFUNLIMITED) {
            try {
                this.spinner.show();

                const promises = [];

                if (this.hasAccess("GetContentUnAgentMarqueeSingleShowList")) {
                    promises.push(this.getUnAgentMarqueeSingleList());
                }

                if (this.hasAccess("GetContentUnAgentMarqueeMultipleShowList")) {
                    promises.push(this.getUnAgentMarqueeMultipleList());
                }

                await Promise.all(promises);

            } catch (error) {
                console.error("Error fetching marquee list:", error);
            } finally {
                this.unAgentMarqueeList = [
                    ...(this.unAgentMarqueeMultipleList.length > 0 ? this.unAgentMarqueeMultipleList : []),
                    ...(this.unAgentMarqueeSingleList.length > 0 ? this.unAgentMarqueeSingleList : [])
                ];

                this.marqueeMessage = this.unAgentMarqueeList
                    .map((m, i) => `${i + 1}. ${m.message}`)
                    .join(' ｜ ');

                this.spinner.hide();
            }

            if (this.marqueeMessage.trim() != '') {
                setTimeout(() => this.setScrollingDuration(), 0);
            }
        }
    }
}
